import { useMutation } from '@apollo/client';

import { QUERY_ROOT_ID, cacheUtils } from '@eluve/apollo-client';
import { InterstitialFlowTypesEnum } from '@eluve/graphql-types';
import { useNamedLogger } from '@eluve/logger';
import { useCaptureEvent } from '@eluve/posthog-react';

import {
  completeInterstitialFlowMutation,
  skipInterstitialFlowMutation,
  userInterstitialFlowsRootFragment,
} from '../operations';

import { InterstitialAnalyticsConfig } from './useInterstitialAnalytics';

interface UseGenericFlowMutationsProps {
  flowName: InterstitialFlowTypesEnum;
  /**
   * Optional PostHog events for this flow. When set, `completedEvent` /
   * `skippedEvent` fire automatically after the corresponding mutation succeeds.
   */
  analytics?: InterstitialAnalyticsConfig;
}

/**
 * Generic hook for completing/skipping any interstitial flow
 * This handles the basic flow state management without business-specific logic
 */
export const useGenericFlowMutations = ({
  flowName,
  analytics,
}: UseGenericFlowMutationsProps) => {
  const logger = useNamedLogger('GenericFlowMutations');
  const captureEvent = useCaptureEvent(analytics?.context);

  const [completeFlowMutation] = useMutation(completeInterstitialFlowMutation, {
    optimisticResponse: (vars) => ({
      completeInterstitialFlow: undefined,
      insertUserInterstitialFlowsOne: {
        __typename: 'UserInterstitialFlows' as const,
        id: `optimistic-${flowName}`,
        flowName,
        completedAt: new Date().toISOString(),
        skippedAt: null,
        data: (vars as { data?: unknown })?.data ?? null,
      },
    }),
    update: (_cache, { data }) => {
      const newItem = data?.insertUserInterstitialFlowsOne;
      if (!newItem) return;
      // Update root fragment list if item missing or needs timestamp update
      cacheUtils.updateFragment(
        {
          fragment: userInterstitialFlowsRootFragment,
          key: { id: QUERY_ROOT_ID },
        },
        (existing) => {
          if (!existing) {
            return existing;
          }
          const idx = existing.userInterstitialFlows.findIndex(
            (f) => f.flowName === flowName,
          );
          if (idx === -1) {
            return {
              userInterstitialFlows: [
                ...existing.userInterstitialFlows,
                newItem,
              ],
            } as typeof existing;
          }
          // Merge update (avoid losing skippedAt if server responded differently)
          const updated = [...existing.userInterstitialFlows];
          updated[idx] = { ...updated[idx], ...newItem };
          return { userInterstitialFlows: updated } as typeof existing;
        },
      );
    },
    onError: (error) => {
      logger.error('Failed to complete flow', { flowName, error });
    },
  });

  const [skipFlowMutation] = useMutation(skipInterstitialFlowMutation, {
    optimisticResponse: () => ({
      insertUserInterstitialFlowsOne: {
        __typename: 'UserInterstitialFlows' as const,
        id: `optimistic-${flowName}`,
        flowName,
        completedAt: null,
        skippedAt: new Date().toISOString(),
        data: null,
      },
    }),
    update: (_cache, { data }) => {
      const newItem = data?.insertUserInterstitialFlowsOne;
      if (!newItem) return;
      cacheUtils.updateFragment(
        {
          fragment: userInterstitialFlowsRootFragment,
          key: { id: QUERY_ROOT_ID },
        },
        (existing) => {
          if (!existing) return existing;
          const idx = existing.userInterstitialFlows.findIndex(
            (f) => f.flowName === flowName,
          );
          if (idx === -1) {
            return {
              userInterstitialFlows: [
                ...existing.userInterstitialFlows,
                newItem,
              ],
            } as typeof existing;
          }
          const updated = [...existing.userInterstitialFlows];
          updated[idx] = { ...updated[idx], ...newItem };
          return { userInterstitialFlows: updated } as typeof existing;
        },
      );
    },
    onError: (error) => {
      logger.error('Failed to skip flow', { flowName, error });
    },
  });

  const completeFlow = async (data?: unknown) => {
    await completeFlowMutation({
      variables: {
        flowName,
        data: data as Record<string, unknown>,
      },
    });
    if (analytics?.completedEvent) {
      captureEvent(analytics.completedEvent, { flowName });
    }
  };

  const skipFlow = async () => {
    await skipFlowMutation({
      variables: {
        flowName,
      },
    });
    if (analytics?.skippedEvent) {
      captureEvent(analytics.skippedEvent, { flowName });
    }
  };

  return {
    completeFlow,
    skipFlow,
  };
};
